import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Paper
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useAppContext } from '../context/AppContext';
import EnergyContext from '../context/EnergyContext';
import { createContextAwareApiClient } from '../api/contextAwareApiClient';
import { energyService } from '../api/energyApi';
import { useAuth } from '../auth/AuthContext';
import { realtimeService } from '../services/realtimeService';
import {
  extractCanopyTelemetryMapFromEnergyLatest,
  matchesCanopyId,
  normalizeTelemetryRecord,
} from '../utils/telemetryRecordNormalizer';
import TelemetryRow from './TelemetryRow';
import FleetTelemetryTable from './FleetTelemetryTable';

const pickNewerTelemetryRecord = (first, second) => {
  if (!first) return second || null;
  if (!second) return first || null;
  const firstHasController = Boolean(first.data?.sabalController);
  const secondHasController = Boolean(second.data?.sabalController);
  if (firstHasController && !secondHasController) return first;
  if (secondHasController && !firstHasController) return second;
  const firstMs = Date.parse(first.timestamp || first.observedAt || '');
  const secondMs = Date.parse(second.timestamp || second.observedAt || '');
  if (Number.isFinite(firstMs) && Number.isFinite(secondMs)) {
    return secondMs >= firstMs ? second : first;
  }
  return Number.isFinite(secondMs) ? second : first;
};

/**
 * MultiCanopyTelemetryView component displays comprehensive telemetry data for multiple selected canopies.
 * Supports table view (summary) and detailed view with expandable JSON payloads.
 * Uses real-time WebSocket updates with fallback to polling when WebSocket fails.
 */
function MultiCanopyTelemetryView({ selectedCanopies = [], canopyNames: propCanopyNames = {} }) {
  const { 
    customerContext,
    fleetContext,
    getContextData,
    validateContext 
  } = useAppContext();
  const auth = useAuth();
  const energyContext = useContext(EnergyContext);
  const energyLatestRef = useRef(null);
  energyLatestRef.current = energyContext?.raw?.latest || null;
  
  // Use ref to store the service to prevent infinite loops
  const telemetryServiceRef = useRef();
  telemetryServiceRef.current = createContextAwareApiClient(getContextData, validateContext).telemetryService;
  
  const [telemetryData, setTelemetryData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('detailed');
  const [isRealTime] = useState(true);

  const resolvedCanopyNames = useMemo(() => {
    const names = { ...propCanopyNames };
    const energyCanopies = energyContext?.model?.canopies || [];
    energyCanopies.forEach((canopy) => {
      if (canopy?.id && canopy?.name && !names[canopy.id]) {
        names[canopy.id] = canopy.name;
      }
    });
    selectedCanopies.forEach((cid) => {
      const recordName = telemetryData[cid]?.canopyName || telemetryData[cid]?.canopy_name;
      if (recordName && !names[cid]) {
        names[cid] = recordName;
      }
    });
    return names;
  }, [propCanopyNames, energyContext?.model?.canopies, selectedCanopies, telemetryData]);

  // Initialize real-time service
  useEffect(() => {
    if (energyContext?.synchronization !== 'commit-journal' && !auth.isGuest) realtimeService.initialize(telemetryServiceRef.current);
  }, [auth.isGuest, energyContext?.synchronization]);

  // Fetch telemetry data for selected canopies
  const fetchTelemetryData = useCallback(async (canopyIds, { background = false } = {}) => {
    if (energyContext?.synchronization === 'commit-journal') {
      setTelemetryData(extractCanopyTelemetryMapFromEnergyLatest(energyLatestRef.current, canopyIds, propCanopyNames));
      setLoading(false); setError(null); return;
    }
    if (!canopyIds || canopyIds.length === 0) {
      setTelemetryData({});
      return;
    }

    const cachedFromEnergy = extractCanopyTelemetryMapFromEnergyLatest(
      energyLatestRef.current,
      canopyIds,
      propCanopyNames
    );

    if (!background) {
      if (Object.keys(cachedFromEnergy).length > 0) {
        setTelemetryData((prev) => ({ ...prev, ...cachedFromEnergy }));
      } else {
        setLoading(true);
      }
      setError(null);
    }
    
    try {
      const energyPromise = Promise.resolve()
        .then(() => (
          typeof energyService?.getLatest === 'function'
            ? energyService.getLatest({
              customerId: customerContext?.customerId,
              fleetId: fleetContext?.fleetId,
              messageType: 'canopy.telemetry',
            })
            : null
        ))
        .catch(() => null);

      const telemetryPromises = canopyIds.map(async (canopyId) => {
        if (auth.isGuest) return { canopyId, response: null };
        try {
          const response = await telemetryServiceRef.current.getLatestTelemetry(canopyId);
          return { canopyId, response };
        } catch (err) {
          console.error(`Failed to fetch telemetry for ${canopyId}:`, err);
          return { canopyId, response: null, error: err.message };
        }
      });

      const [energyResponse, ...legacyResults] = await Promise.all([
        energyPromise,
        ...telemetryPromises,
      ]);

      const energyMap = energyResponse && energyResponse.success !== false
        ? extractCanopyTelemetryMapFromEnergyLatest(energyResponse, canopyIds, propCanopyNames)
        : {};
      
      // Organize results by canopy ID
      const newTelemetryData = {};
      legacyResults.forEach(({ canopyId, response, error: legacyError }) => {
        const fallbackIdentity = {
          customerId: customerContext?.customerId,
          fleetId: fleetContext?.fleetId,
          canopyId,
          canopyName: propCanopyNames[canopyId],
        };
        const legacyRecord = response && response.success !== false
          ? normalizeTelemetryRecord(response, fallbackIdentity)
          : null;
        const energyRecord = energyMap[canopyId] || null;
        const cachedRecord = cachedFromEnergy[canopyId] || null;
        const resolved = pickNewerTelemetryRecord(
          pickNewerTelemetryRecord(legacyRecord, energyRecord),
          cachedRecord
        );

        if (resolved) {
          newTelemetryData[canopyId] = resolved;
        } else if (legacyError && !energyResponse?.success) {
          newTelemetryData[canopyId] = { error: legacyError };
        } else if (response && response.success === false && response.status !== 404 && !energyResponse?.success) {
          newTelemetryData[canopyId] = { error: response.message || 'Failed to load telemetry' };
        } else {
          newTelemetryData[canopyId] = { data: null };
        }
      });
      
      setTelemetryData((prev) => (background ? { ...prev, ...newTelemetryData } : newTelemetryData));
    } catch (err) {
      console.error('💥 MULTI CANOPY TELEMETRY: Error fetching telemetry:', err);
      if (!background && Object.keys(cachedFromEnergy).length === 0) {
        setError(`Error fetching telemetry data: ${err.message}`);
      }
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, [customerContext?.customerId, fleetContext?.fleetId, propCanopyNames, auth.isGuest, energyContext?.synchronization]);

  // Fetch telemetry when selected canopies change
  useEffect(() => {
    fetchTelemetryData(selectedCanopies);
  }, [selectedCanopies, fetchTelemetryData]);

  // Keep fleet telemetry updated when EnergyContext refreshes
  useEffect(() => {
    if (selectedCanopies.length === 0 || !energyContext?.raw?.latest) return;
    const latestMap = extractCanopyTelemetryMapFromEnergyLatest(
      energyContext.raw.latest,
      selectedCanopies,
      propCanopyNames
    );
    if (Object.keys(latestMap).length > 0) {
      setTelemetryData((prev) => {
        const next = { ...prev };
        Object.entries(latestMap).forEach(([canopyId, record]) => {
          const existing = next[canopyId]?.data ? next[canopyId] : null;
          next[canopyId] = pickNewerTelemetryRecord(existing, record);
        });
        return next;
      });
    }
  }, [selectedCanopies, energyContext?.raw?.latest, propCanopyNames]);

  // Real-time updates using WebSocket + 10-second polling
  useEffect(() => {
    if (selectedCanopies.length === 0 || energyContext?.synchronization === 'commit-journal') return;

    const pollInterval = setInterval(() => {
      fetchTelemetryData(selectedCanopies, { background: true });
    }, 10000);

    if (isRealTime && !auth.isGuest) {
      // Connect to WebSocket if not already connected
      realtimeService.connect();
      
      const handler = (data) => {
        const matchedCanopyId = selectedCanopies.find((canopyId) => matchesCanopyId(data, canopyId));
        if (matchedCanopyId) {
          const normalized = normalizeTelemetryRecord(data, {
            customerId: customerContext?.customerId,
            fleetId: fleetContext?.fleetId,
            canopyId: matchedCanopyId,
            canopyName: propCanopyNames[matchedCanopyId],
          });
          if (normalized) {
            setTelemetryData((prev) => ({
              ...prev,
              [matchedCanopyId]: normalized,
            }));
            setError(null);
          }
        }
      };

      realtimeService.subscribe('telemetry_update', handler);

      // Cleanup function
      return () => {
        clearInterval(pollInterval);
        realtimeService.unsubscribe('telemetry_update', handler);
      };
    }

    return () => {
      clearInterval(pollInterval);
    };
  }, [
    selectedCanopies,
    customerContext?.customerId,
    fleetContext?.fleetId,
    propCanopyNames,
    isRealTime,
    auth.isGuest,
    fetchTelemetryData,
    energyContext?.synchronization,
  ]);

  // Handle view mode change
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Format timestamp
  const formatTimestamp = (telemetry) => {
    const rawTs = telemetry?.timestamp || telemetry?.observedAt || telemetry?.data?.timestamp;
    if (!rawTs) return 'N/A';
    
    try {
      const date = new Date(rawTs);
      return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleTimeString();
    } catch (e) {
      return 'N/A';
    }
  };

  const formatCanopyHeading = (canopyId) => {
    const displayName = resolvedCanopyNames[canopyId];
    if (displayName && displayName !== canopyId) {
      return `${displayName} (${canopyId})`;
    }
    return canopyId;
  };

  if (selectedCanopies.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="textSecondary">
          Select up to 3 canopies to view telemetry data
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading telemetry data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* View mode toggle only */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
        >
          <ToggleButton value="detailed" aria-label="detailed view">
            <TimelineIcon sx={{ fontSize: 16, mr: 0.5 }} />
            <Typography variant="caption">Detailed</Typography>
          </ToggleButton>
          <ToggleButton value="summary" aria-label="summary view">
            <TableChartIcon sx={{ fontSize: 16, mr: 0.5 }} />
            <Typography variant="caption">Summary</Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {/* Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {viewMode === 'detailed' ? (
          /* Detailed View - Shows full telemetry data with expandable JSON */
          <Box sx={{ p: 2 }}>
            {selectedCanopies.map((canopyId, index) => {
              const telemetry = telemetryData[canopyId];
              
              if (!telemetry) {
                return (
                  <React.Fragment key={canopyId}>
                    <Paper sx={{ p: 2, mb: 2 }}>
                      <Typography variant="h6" gutterBottom>{formatCanopyHeading(canopyId)}</Typography>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ mt: 1 }}>Loading data...</Typography>
                    </Paper>
                    {index < selectedCanopies.length - 1 && <Divider sx={{ mb: 2 }} />}
                  </React.Fragment>
                );
              }
              
              if (telemetry?.error) {
                return (
                  <React.Fragment key={canopyId}>
                    <Paper sx={{ p: 2, mb: 2, border: '1px solid #ffcdd2', bgcolor: '#ffebee' }}>
                      <Typography variant="h6" color="error" gutterBottom>
                        {formatCanopyHeading(canopyId)}
                      </Typography>
                      <Typography variant="body2" color="error">
                        Error: {telemetry.error}
                      </Typography>
                    </Paper>
                    {index < selectedCanopies.length - 1 && <Divider sx={{ mb: 2 }} />}
                  </React.Fragment>
                );
              }
              
              if (!telemetry.data) {
                return (
                  <React.Fragment key={canopyId}>
                    <Paper sx={{ p: 2, mb: 2 }}>
                      <Typography variant="h6" gutterBottom>{formatCanopyHeading(canopyId)}</Typography>
                      <Typography>No telemetry data available.</Typography>
                    </Paper>
                    {index < selectedCanopies.length - 1 && <Divider sx={{ mb: 2 }} />}
                  </React.Fragment>
                );
              }
              
              return (
                <React.Fragment key={canopyId}>
                  <Paper sx={{ mb: 2, overflow: 'hidden' }}>
                    {/* Canopy Header */}
                    <Box sx={{ p: 2, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="h6" gutterBottom>
                        {formatCanopyHeading(canopyId)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Last updated: {formatTimestamp(telemetry)}
                      </Typography>
                    </Box>
                    
                    {/* Telemetry Data with expandable JSON */}
                    <TelemetryRow telemetry={telemetry} />
                  </Paper>
                  {index < selectedCanopies.length - 1 && <Divider sx={{ mb: 2 }} />}
                </React.Fragment>
              );
            })}
          </Box>
        ) : (
          /* Summary View - Shows key metrics in a compact table */
          <Box sx={{ p: 2 }}>
            <FleetTelemetryTable
              selectedCanopies={selectedCanopies}
              telemetryData={telemetryData}
              canopyNames={Object.fromEntries(selectedCanopies.map((cid) => [
                cid,
                resolvedCanopyNames[cid] || telemetryData[cid]?.canopyName || telemetryData[cid]?.canopy_id || cid,
              ]))}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default MultiCanopyTelemetryView;